import { useQuery } from '@apollo/react-hooks';
import React, { useContext, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { UniversalContext } from '../../../../_universal';
import { withCustomContexts } from '../../../../shared/components/Contexts';
import {
  DiGenericLoadingPage,
  DiLayout,
  DiNotFoundPage,
  LmHeader,
} from '../../../../shared/components/DiLayout';
import { createSchedulingSessionStateLandingPage } from '../../../../shared/components/SchedulingSessionState';
import { LM_CLIENT_CONTEXT } from '../../../../shared/constants';
import { trackAffiliateEvent } from '../../../../shared/utils/affiliateTracking';
import isForbidden from '../../../../shared/utils/isForbidden';
import { IRouteProps, ITrackerData, IWidgetSettings } from '../../../../types';
import WidgetSessionOpenedMutation from '../../../components/WidgetSessionOpenedMutation';
import { SchedulingErrorPage, SchedulingNotFoundPage } from '../components';
import SchedulingPageBody from '../components/SchedulingPageBody';
import { TrackSchedulingEvent } from '../types';
import query from './NewProviderSchedulingPageQuery.graphql';
import AnnouncementBanner from '../../../components/AnnouncementBanner';

interface IProps extends IRouteProps {
  widgetSettings: IWidgetSettings;
  trackerData: ITrackerData;
  prefillToken: string;
}

function NewProviderSchedulingPage({
  history,
  match,
  trackerData,
  widgetSettings,
  prefillToken,
}: IProps) {
  const { id } = match.params;
  const { trackEvent } = useContext(UniversalContext);
  const trackedRef = useRef(false);

  const { data, loading, error, refetch } = useQuery(query, {
    variables: { id },
    context: LM_CLIENT_CONTEXT,
  });

  const { provider } = data || {};

  useEffect(() => {
    if (loading || trackedRef.current) return;

    if (provider) {
      trackEvent({
        event: 'Viewed Provider',
        properties: {
          widget: true,
          providerId: provider.id,
          providerDisplay: provider.display,
        },
      });
    } else {
      trackEvent({
        event: 'Provider Widget Not Found',
        properties: { providerId: id },
      });
    }

    trackEvent({ event: 'Viewed Widget Version 2' });
    trackAffiliateEvent({
      aftId: widgetSettings.aftId,
      aftType: widgetSettings.aftType,
      aft: widgetSettings.aft,
      event: 'Viewed LocalMed Widget',
    });

    trackedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (loading) return <DiGenericLoadingPage />;

  if (isForbidden(error)) {
    return <DiNotFoundPage />;
  }

  if (error) {
    return <SchedulingErrorPage refetch={refetch} />;
  }

  if (!provider) {
    return <SchedulingNotFoundPage />;
  }

  const { office } = provider;
  const { announcementEnabled, announcement } = office || {};

  const trackSchedulingEvent: TrackSchedulingEvent = (
    event,
    component = 'ProviderSchedulingPage',
    additionalProperties = {}
  ) => {
    trackEvent({
      event,
      properties: {
        workflow: 'Provider Scheduling',
        component,
        providerId: provider.id,
        providerDisplay: provider.display,
        practiceId: office.practice.id,
        practiceDisplay: office.practice.display,
        widget: true,
        ...additionalProperties,
      },
    });
  };

  return (
    <DiLayout
      header={<LmHeader office={office} practice={office.practice} />}
      footerText="Powered by LocalMed"
      fullWidth
    >
      <Helmet>
        <title>
          {`Schedule an Appointment | ${office.messagingName || office.practice.display}`}
        </title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <WidgetSessionOpenedMutation widgetId={trackerData?.widgetId} />
      {announcementEnabled && (
        <AnnouncementBanner
          announcement={announcement}
          backgroundColor="lightOrange"
          buttonColor="black"
          hideIcon
        />
      )}
      <SchedulingPageBody
        history={history}
        office={provider.office}
        provider={provider}
        trackSchedulingEvent={trackSchedulingEvent}
        trackerData={trackerData}
        widgetSettings={widgetSettings}
        prefillToken={prefillToken}
      />
    </DiLayout>
  );
}

export default createSchedulingSessionStateLandingPage(
  withCustomContexts(NewProviderSchedulingPage)
);
